export interface AnchoredSelectionDraft {
  selectedText: string;
  selectedHash: string;
  startOffset: number;
  endOffset: number;
  blockIndex: number;
  prefixText: string;
  suffixText: string;
  contextText: string;
}

export type AnchoredSelectionIssueCode =
  | "CROSS_GIST_SELECTION"
  | "MULTI_BLOCK_SELECTION"
  | "INVALID_SELECTION";

export interface AnchoredSelectionIssue {
  code: AnchoredSelectionIssueCode;
  message: string;
}

export interface AnchoredSelectionResult {
  draft: AnchoredSelectionDraft | null;
  issue: AnchoredSelectionIssue | null;
}

const MAX_SELECTED_TEXT = 10000;
const MAX_CONTEXT_TEXT = 12000;
const CONTEXT_WINDOW = 240;
const PREFIX_SUFFIX_WINDOW = 160;

function normalizeInlineText(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function clampText(value: string, maxLength: number): string {
  const normalized = normalizeInlineText(value);
  return normalized.length > maxLength ? normalized.slice(0, maxLength) : normalized;
}

function stableHash(value: string): string {
  let hash = 5381;

  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) + hash) ^ value.charCodeAt(index);
  }

  return `djb2:${(hash >>> 0).toString(16)}`;
}

function getElementFromNode(node: Node | null): HTMLElement | null {
  if (!node) return null;
  if (node.nodeType === Node.ELEMENT_NODE) return node as HTMLElement;
  return node.parentElement;
}

function getSelectionBlock(node: Node | null): HTMLElement | null {
  return getElementFromNode(node)?.closest(".annotated-body-block") as HTMLElement | null;
}

function getBlockContent(block: HTMLElement | null): HTMLElement | null {
  return block?.querySelector(".annotated-body-block-content") as HTMLElement | null;
}

function getBlockIndex(block: HTMLElement | null): number | null {
  const rawIndex = block?.getAttribute("data-block-index");
  if (!rawIndex) return null;

  const parsed = Number.parseInt(rawIndex, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function getSelectionOffsets(contentElement: HTMLElement, range: Range): { startOffset: number; endOffset: number } | null {
  try {
    const startRange = document.createRange();
    startRange.selectNodeContents(contentElement);
    startRange.setEnd(range.startContainer, range.startOffset);

    const endRange = document.createRange();
    endRange.selectNodeContents(contentElement);
    endRange.setEnd(range.endContainer, range.endOffset);

    const startOffset = startRange.toString().length;
    const endOffset = endRange.toString().length;

    if (!Number.isFinite(startOffset) || !Number.isFinite(endOffset) || endOffset <= startOffset) {
      return null;
    }

    return { startOffset, endOffset };
  } catch (error) {
    console.warn("Failed to calculate anchored selection offsets", error);
    return null;
  }
}

function buildContext(blockText: string, startOffset: number, endOffset: number): string {
  const contextStart = Math.max(0, startOffset - CONTEXT_WINDOW);
  const contextEnd = Math.min(blockText.length, endOffset + CONTEXT_WINDOW);
  return clampText(blockText.slice(contextStart, contextEnd), MAX_CONTEXT_TEXT);
}

/**
 * Converts the active browser selection into the minimal backend anchor payload,
 * plus a user-facing diagnostic when the current MVP cannot safely create an anchor.
 *
 * MVP rules:
 * - selection must start and end inside the same rendered body block;
 * - selection must belong to one read-only AnnotatedBody shell;
 * - body HTML stays clean; we only save anchor metadata.
 */
export function getAnchoredSelectionResult(container: HTMLElement | null): AnchoredSelectionResult {
  if (!container) return { draft: null, issue: null };

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
    return { draft: null, issue: null };
  }

  const range = selection.getRangeAt(0);
  const selectedText = clampText(range.toString(), MAX_SELECTED_TEXT);
  if (!selectedText) return { draft: null, issue: null };

  const startBlock = getSelectionBlock(range.startContainer);
  const endBlock = getSelectionBlock(range.endContainer);
  const startInsideContainer = Boolean(startBlock && container.contains(startBlock));
  const endInsideContainer = Boolean(endBlock && container.contains(endBlock));

  if (!startInsideContainer && !endInsideContainer) {
    return { draft: null, issue: null };
  }

  if (!startInsideContainer || !endInsideContainer) {
    return {
      draft: null,
      issue: {
        code: "CROSS_GIST_SELECTION",
        message: "Select text inside one visible Gist body to start an anchored discussion.",
      },
    };
  }

  if (!startBlock || !endBlock || startBlock !== endBlock) {
    return {
      draft: null,
      issue: {
        code: "MULTI_BLOCK_SELECTION",
        message: "For now, select text inside one paragraph/block to start an anchored discussion. Multi-paragraph annotations will be added in a later train.",
      },
    };
  }

  const blockIndex = getBlockIndex(startBlock);
  const contentElement = getBlockContent(startBlock);
  if (blockIndex === null || !contentElement) {
    return {
      draft: null,
      issue: {
        code: "INVALID_SELECTION",
        message: "This text selection cannot be converted into an anchored discussion. Try selecting a smaller passage inside one paragraph.",
      },
    };
  }

  const offsets = getSelectionOffsets(contentElement, range);
  if (!offsets) {
    return {
      draft: null,
      issue: {
        code: "INVALID_SELECTION",
        message: "This text selection cannot be converted into an anchored discussion. Try selecting a smaller passage inside one paragraph.",
      },
    };
  }

  const blockText = contentElement.textContent || "";
  const prefixText = clampText(
    blockText.slice(Math.max(0, offsets.startOffset - PREFIX_SUFFIX_WINDOW), offsets.startOffset),
    1000
  );
  const suffixText = clampText(
    blockText.slice(offsets.endOffset, Math.min(blockText.length, offsets.endOffset + PREFIX_SUFFIX_WINDOW)),
    1000
  );

  return {
    draft: {
      selectedText,
      selectedHash: stableHash(selectedText),
      startOffset: offsets.startOffset,
      endOffset: offsets.endOffset,
      blockIndex,
      prefixText,
      suffixText,
      contextText: buildContext(blockText, offsets.startOffset, offsets.endOffset),
    },
    issue: null,
  };
}

export function getAnchoredSelectionDraft(container: HTMLElement | null): AnchoredSelectionDraft | null {
  return getAnchoredSelectionResult(container).draft;
}

export function clearBrowserTextSelection(): void {
  window.getSelection()?.removeAllRanges();
}
